export const resolveExternalUserId = (candidate: unknown): string | null => {
  if (typeof candidate === 'string' && candidate.trim()) return candidate;
  if (typeof candidate === 'number' && Number.isFinite(candidate)) return String(candidate);
  if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
    const obj = candidate as Record<string, unknown>;
    const id = obj.userId ?? obj.id ?? obj.externalId ?? obj.external_id;
    if (typeof id === 'string' && id.trim()) return id;
    if (typeof id === 'number' && Number.isFinite(id)) return String(id);
  }
  return null;
};

export const resolveIdentifyTraits = (args: unknown[]): Record<string, unknown> => {
  let traits: Record<string, unknown> = {};
  const first = args[0];
  const second = args[1];
  if (first && typeof first === 'object' && !Array.isArray(first)) {
    traits = { ...(first as Record<string, unknown>) };
  }
  if (second && typeof second === 'object' && !Array.isArray(second)) {
    traits = { ...traits, ...(second as Record<string, unknown>) };
  }
  return traits;
};

/**
 * Mantém o `identify` do visitante sempre interceptado, mesmo quando outro dono o reatribui.
 *
 * O guia faz exatamente isso: ao terminar de subir, `GuideManager.processQueue()` executa
 * `window.Getdemo.identify = this.identifyUser` e ainda esvazia a fila `q`. Antes, o loader
 * reinstalava o seu wrapper num intervalo de 500 ms — então entre a reatribuição do guia e o
 * tick seguinte havia uma janela em que o `identify` era a função crua. Uma chamada caída ali
 * chegava ao guia e sumia para o Copilot: não atualizava o snapshot, não disparava a
 * re-resolução da audiência, e não sobrava na fila (o guia já a tinha drenado). O agente ficava
 * preso no estado "sem identify" até o próximo F5 — a intermitência relatada pela Getrak.
 *
 * Com um acessor, a reatribuição é capturada no mesmo instante: o `set` guarda a implementação
 * nova e devolve um wrapper por cima dela. A janela deixa de existir em vez de só encolher.
 *
 * Devolve `false` quando o acessor não pôde ser instalado (objeto congelado, por exemplo); nesse
 * caso quem chama deve manter a reinstalação periódica como rede de segurança.
 */
export const installIdentifyInterceptor = (
  target: any,
  onIdentify: (args: unknown[]) => void
): boolean => {
  if (!target || (typeof target !== 'object' && typeof target !== 'function')) return false;

  const wrap = (raw: unknown): any => {
    // Nunca embrulhar um wrapper nosso: `raw` vira a implementação original dele.
    const inner = raw && (raw as any).__gdWrapped ? (raw as any).__gdRaw : raw;
    const wrapped = function (this: unknown, ...args: unknown[]) {
      const result = typeof inner === 'function' ? (inner as any).apply(this, args) : undefined;
      onIdentify(args);
      return result;
    };
    (wrapped as any).__gdWrapped = true;
    (wrapped as any).__gdRaw = inner;
    return wrapped;
  };

  try {
    const existing = Object.getOwnPropertyDescriptor(target, 'identify');
    if (existing && typeof existing.get === 'function' && (existing.get as any).__gdAccessor) {
      return true; // já interceptado neste objeto
    }
    if (existing && !existing.configurable) return false;

    let current = wrap(existing ? existing.value ?? existing.get?.call(target) : undefined);
    const get = function () {
      return current;
    };
    (get as any).__gdAccessor = true;

    Object.defineProperty(target, 'identify', {
      configurable: true,
      enumerable: true,
      get,
      set(next: unknown) {
        current = wrap(next);
      }
    });
    return true;
  } catch {
    return false;
  }
};
